/* Libraries */
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Color132, Color201, Color401, Color612, ColorDarkuiAccent3 } from 'ui-connect/styles/default-theme';

/* Components */
import MenuItem from './components/MenuItem';

/* Utils */
import { useUserType } from 'utils/navigation/helpers';
import { getMenuJson } from 'utils/navigation';
import NavigationUtil from 'utils/NavigationUtil';
import { getClassNames as cx } from 'utils/ReactUtil';
import LocaleMapper from 'utils/LocaleMapper';

/* Redux */
import { getUserSocialProfile, isAlphaUser } from 'reducers/user/userSocialProfile';
import { getViewerUserPreferences } from 'reducers/viewerUserPreferences/viewerUserPreferences';
import { fetchMFPLinkStatus, getMFPLinkStatus } from 'reducers/mfp/MFPLinkStatus';
import { fetchSystemPreference } from 'actions';
import { enrollInAlpha, unenrollInAlpha } from 'actions/betaTestingActions';
import { fetchUserSocialProfile, fetchViewerUserPreferences } from 'actions/settingsActions';

/* Style */
import styles from './MainSidebar.module.css';

const mapStateToProps = (state, { displayName }) => ({
    isAlphaUser: isAlphaUser(state, displayName),
    userSocialProfile: getUserSocialProfile(state, displayName),
    viewerUserPreferences: getViewerUserPreferences(state),
    mfpLinkDetails: getMFPLinkStatus(state)
});

const mapDispatchToProps = {
    fetchSystemPreference,
    fetchUserSocialProfile,
    fetchViewerUserPreferences,
    fetchMFPLinkStatus
};

const useMainSidebarViewConstants = ({ viewerUserPreferences }) => {
    const isBetaPool = !!document.querySelector('.beta-tag');
    const locale = LocaleMapper.getFullLocale(viewerUserPreferences?.preferredLocale);
    const activityTrackingAccuracyUrl = `https://www.garmin.com/${locale}/legal/atdisclaimer`;

    return {
        isBetaPool,
        activityTrackingAccuracyUrl
    };
};

const MainSidebar = ({
    preferences,
    usageIndicators,
    newsFeedStatus,
    advancedReporting = null,
    setCollapsed,
    isCollapsed = true,
    isMobile,
    displayName,
    mfpLinkDetails,
    isAlphaUser,
    // redux
    userSocialProfile,
    fetchSystemPreference,
    fetchUserSocialProfile,
    fetchViewerUserPreferences,
    viewerUserPreferences
}) => {
    const { t, ready } = useTranslation(['send_to_device', 'common']);
    const { isBetaPool, activityTrackingAccuracyUrl } = useMainSidebarViewConstants({
        viewerUserPreferences
    });

    const [initialCollapsed] = useState(isCollapsed);
    const [initialCollapsedSet, setInitialCollapsedSet] = useState(false);

    useEffect(() => {
        if (!initialCollapsedSet && isCollapsed !== initialCollapsed) {
            setInitialCollapsedSet(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCollapsed]);

    // Run this every render
    useEffect(() => {
        const betaTag = document.querySelector('.beta-tag');
        if (betaTag) {
            betaTag.style.display = isBetaPool ? 'block' : undefined;
        }
    });

    const userType = useUserType(userSocialProfile);
    const { dashboards, main, social, other } = getMenuJson({
        preferences,
        mfp: mfpLinkDetails,
        usageIndicators,
        advancedReporting,
        userType
    });

    const menuItems = useMemo(
        () => [
            {
                items: dashboards,
                color: Color132,
                hasNewsfeedStatus: true
            },
            {
                items: main,
                color: Color201
            },
            {
                items: social,
                color: Color612
            },
            {
                items: other,
                color: Color401
            }
        ],
        [dashboards, main, other, social]
    );

    const renderItems = (items, iconColor, hasNewsfeedStatus = false) =>
        items.map((item) => (
            <MenuItem
                iconColor={iconColor}
                isCollapsed={isCollapsed}
                setCollapsed={setCollapsed}
                newsFeedStatus={hasNewsfeedStatus ? newsFeedStatus : undefined}
                {...item}
                key={item.title}
                forceClear={!initialCollapsedSet ? initialCollapsed : false}
            />
        ));

    const handleBetaClick = () => (isBetaPool ? unenrollInAlpha() : enrollInAlpha());

    useEffect(() => {
        fetchSystemPreference();
        fetchMFPLinkStatus();
        fetchUserSocialProfile(displayName, true);
        fetchViewerUserPreferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <a className="logo" href={NavigationUtil.url('/')} title={t('send_to_device:gc')}>
                <span className="mobile-logo"></span>
            </a>

            <div className={cx(styles.sidebarMenuGroups, isCollapsed && styles.collapsed, isMobile && styles.mobile)}>
                {menuItems.map(({ items, color, hasNewsfeedStatus }, index) => (
                    <ul key={index} className={styles.sidebarMenuItems}>
                        {renderItems(items, color, hasNewsfeedStatus)}
                        <li className={styles.groupBottomBorder} />
                    </ul>
                ))}

                <ul className={styles.sidebarMenuItems}>
                    <MenuItem
                        key="info"
                        iconColor={ColorDarkuiAccent3}
                        url={activityTrackingAccuracyUrl}
                        isCollapsed={isCollapsed}
                        setCollapsed={setCollapsed}
                        icon="info-2"
                        target="_blank"
                        title="activity_tracking_accuracy"
                        translationNamespace="main_container"
                        forceClear={!initialCollapsedSet ? initialCollapsed : false}
                    />
                </ul>

                {!isCollapsed && isAlphaUser && ready && (
                    <button onClick={handleBetaClick}>{t(`common:${isBetaPool ? 'beta_leave' : 'beta_join'}`)}</button>
                )}

                {/* for consent modal */}
                <span className="empty-for-empty-react-component" />
            </div>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MainSidebar);
